import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';

const Ads: React.FC = () => {
  const { t } = useTranslation('ads');
  const backendList = t('backendList', { returnObjects: true }) as Array<{ icon: string; text: string }>;

  return (
    <div className="min-h-screen pt-24">
      <section className="relative overflow-hidden page-section">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(139,92,246,0.12),transparent_70%)] opacity-60" />
        <div className="absolute inset-0 bg-grid-pattern opacity-[0.07]" />

        <div className="relative z-10 container mx-auto px-6">
          <PageHero title={t('heroTitle')} subtitle={t('heroSubtitle')} />
        </div>
      </section>

      <section className="page-section">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="page-max space-y-12"
          >
            <motion.div className="rounded-2xl border border-primary-500/25 bg-dark-800/50 p-8 shadow-xl backdrop-blur-sm">
              <p className="mb-6 text-lg leading-relaxed text-dark-300">{t('introP1')}</p>
              <p className="mb-6 text-lg leading-relaxed text-dark-300">{t('introP2')}</p>
              <p className="text-lg leading-relaxed text-dark-300">{t('introP3')}</p>
            </motion.div>

            <motion.div
              className="group relative overflow-hidden rounded-2xl border border-purple-500/30 bg-gradient-to-br from-dark-800 to-dark-900 p-8 shadow-2xl"
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.75 }}
              viewport={{ once: true }}
            >
              <span className="pointer-events-none absolute -right-24 -top-24 h-56 w-56 rounded-full bg-gradient-to-br from-primary-500/15 to-secondary-500/10 blur-3xl transition-opacity group-hover:opacity-100" />
              <h2 className="mb-6 flex items-center text-3xl font-bold gradient-text">
                <span className="mr-3 text-purple-400">🧠</span>
                {t('backendTitle')}
              </h2>
              <p className="mb-6 text-lg leading-relaxed text-dark-300">{t('backendLead')}</p>
              <p className="mb-6 text-lg leading-relaxed text-dark-300">{t('backendP1')}</p>
              <p className="mb-4 text-lg text-dark-200">{t('backendP2')}</p>
              <ul className="mb-6 space-y-4">
                {backendList.map((row, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="text-xl text-purple-400">{row.icon}</span>
                    <span className="text-lg text-dark-300">{row.text}</span>
                  </li>
                ))}
              </ul>
              <p className="mb-6 text-lg leading-relaxed text-dark-300">{t('backendP3')}</p>
              <p className="text-lg leading-relaxed text-dark-300">{t('backendP4')}</p>
            </motion.div>

            <motion.div
              className="group relative overflow-hidden rounded-2xl border border-purple-500/30 bg-gradient-to-br from-dark-800 to-dark-900 p-8 shadow-2xl"
              initial={{ opacity: 0, x: 24 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.75 }}
              viewport={{ once: true }}
            >
              <span className="pointer-events-none absolute -bottom-24 -left-24 h-56 w-56 rounded-full bg-gradient-to-br from-secondary-500/15 to-primary-500/10 blur-3xl opacity-80" />
              <h2 className="mb-6 flex items-center text-3xl font-bold gradient-text">
                <span className="mr-3 text-purple-400">🚀</span>
                {t('nextTitle')}
              </h2>
              <p className="mb-6 text-lg leading-relaxed text-dark-300">{t('nextP1')}</p>
              <p className="mb-6 text-lg leading-relaxed text-dark-300">{t('nextP2')}</p>
              <p className="mb-6 text-lg leading-relaxed text-dark-300">{t('nextP3')}</p>
              <p className="text-lg leading-relaxed text-dark-300">{t('nextP4')}</p>
            </motion.div>

            <motion.div
              className="rounded-2xl border border-green-500/30 bg-gradient-to-br from-dark-800 to-dark-900 p-8 shadow-2xl"
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.75 }}
              viewport={{ once: true }}
            >
              <h2 className="mb-6 flex items-center text-3xl font-bold gradient-text">
                <span className="mr-3 text-green-400">💬</span>
                {t('whenTitle')}
              </h2>
              <p className="mb-6 text-lg leading-relaxed text-dark-300">{t('whenP1')}</p>
              <p className="text-lg leading-relaxed text-dark-300">{t('whenP2')}</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="page-section border-t border-primary-500/10 bg-[#14082a]/60">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="page-max"
          >
            <h3 className="mb-4 text-2xl font-bold gradient-text">{t('ctaTitle')}</h3>
            <p className="text-dark-300">{t('ctaText')}</p>
            <Link
              to="/contact"
              className="mt-6 inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-primary-600/90 to-secondary-600/90 px-8 py-3 font-semibold text-purple-100 shadow-lg ring-1 ring-primary-500/30 transition hover:ring-primary-400/50"
            >
              {t('contactCta')}
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Ads;
